import { useRef, useState, useCallback } from 'react'
import useSpeechDialogStore from '@/components/SpeechDialog/speech-dialog.store.ts'
import type Recorder from 'recorder-js'

let recorder: Recorder | null = null

// Convert WebM audio blob to WAV format
const convertWebMToWAV = async (webmBlob: Blob): Promise<Blob> =>
  await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = async () => {
      try {
        const arrayBuffer = reader.result
        if (!arrayBuffer || typeof arrayBuffer === 'string') {
          throw new Error('Failed to read audio data')
        }

        const audioContext = new AudioContext()

        // Decode the WebM audio data
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

        // Convert to WAV format
        const wavBuffer = audioBufferToWav(audioBuffer)
        const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' })

        resolve(wavBlob)
      } catch (error) {
        console.error('Audio conversion failed:', error)
        reject(error instanceof Error ? error : new Error('Unknown error'))
      }
    }
    reader.onerror = () => {
      reject(new Error('Failed to read audio blob'))
    }
    reader.readAsArrayBuffer(webmBlob)
  })

// Convert AudioBuffer to WAV format
const audioBufferToWav = (buffer: AudioBuffer): ArrayBuffer => {
  const length = buffer.length
  const numberOfChannels = buffer.numberOfChannels
  const sampleRate = buffer.sampleRate
  const bitsPerSample = 16
  const bytesPerSample = bitsPerSample / 8
  const blockAlign = numberOfChannels * bytesPerSample
  const byteRate = sampleRate * blockAlign
  const dataSize = length * blockAlign
  const bufferLength = 44 + dataSize

  const arrayBuffer = new ArrayBuffer(bufferLength)
  const view = new DataView(arrayBuffer)

  // WAV header
  const writeString = (offset: number, string: string): void => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i))
    }
  }

  writeString(0, 'RIFF')
  view.setUint32(4, bufferLength - 8, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true) // fmt chunk size
  view.setUint16(20, 1, true) // PCM format
  view.setUint16(22, numberOfChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitsPerSample, true)
  writeString(36, 'data')
  view.setUint32(40, dataSize, true)

  // Convert audio data
  let offset = 44
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = Math.max(
        -1,
        Math.min(1, buffer.getChannelData(channel)[i] ?? 0)
      )
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff
      view.setInt16(offset, intSample, true)
      offset += 2
    }
  }

  return arrayBuffer
}

export function useRecorder(): {
  isRecording: boolean
  audioUrl: string | undefined
  recordingTime: number
  countdown: number | undefined
  handleRecordClick: () => void
  startRecording: () => void
  stopRecording: () => void
  settingsOpen: boolean
  setSettingsOpen: (open: boolean) => void
  frameRef: React.RefObject<HTMLDivElement>
  formatTime: (seconds: number) => string
  handleClose: () => void
  streamRef: React.RefObject<MediaStream | undefined>
} {
  const {
    isRecording,
    setIsRecording,
    setRecordOpen,
    setRecordedAudio,
    countIn,
    stereo,
    autoGain,
    noiseSuppression,
    echoCancel,
    countdown,
    setCountdown,
  } = useSpeechDialogStore()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const frameRef = useRef<HTMLDivElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | undefined>(undefined)
  const [audioUrl, setAudioUrl] = useState<string | undefined>(undefined)
  const [recordingTime, setRecordingTime] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout>()
  const countdownIntervalRef = useRef<NodeJS.Timeout>()
  const streamRef = useRef<MediaStream>()
  const shouldSaveRecordingRef = useRef<boolean>(false)

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const startRecorderWithStream = (stream: MediaStream): void => {
    setRecordedAudio(null)
    const chunks: Blob[] = []
    const mediaRecorder = new window.MediaRecorder(stream)
    mediaRecorderRef.current = mediaRecorder
    mediaRecorder.ondataavailable = (e: BlobEvent) => {
      if (e.data.size > 0) chunks.push(e.data)
    }
    mediaRecorder.onstop = async () => {
      // Create blob with proper WebM type first
      const webmBlob = new Blob(chunks, { type: 'audio/webm' })

      try {
        // Convert WebM to WAV
        console.info('Converting WebM to WAV...', {
          originalSize: webmBlob.size,
          originalType: webmBlob.type,
        })
        const wavBlob = await convertWebMToWAV(webmBlob)
        console.info('WAV conversion successful:', {
          wavSize: wavBlob.size,
          wavType: wavBlob.type,
        })
        const url = URL.createObjectURL(wavBlob)
        setAudioUrl(url)
        if (shouldSaveRecordingRef.current) {
          setRecordedAudio(url)
        }
      } catch (error) {
        console.error('Failed to convert audio to WAV:', error)
        // Fallback to original blob if conversion fails
        const url = URL.createObjectURL(webmBlob)
        setAudioUrl(url)
        if (shouldSaveRecordingRef.current) {
          setRecordedAudio(url)
        }
      }

      setRecordOpen(false)
      if (intervalRef.current) clearInterval(intervalRef.current)
      mediaRecorderRef.current = undefined
    }
    mediaRecorder.start()
    intervalRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1)
    }, 1000)
  }

  const startRecording = (): void => {
    setAudioUrl(undefined)
    setRecordingTime(0)
    setIsRecording(true)
    shouldSaveRecordingRef.current = true
    if (!streamRef.current) {
      void navigator.mediaDevices
        .getUserMedia({
          audio: {
            channelCount: stereo ? 2 : 1,
            autoGainControl: autoGain,
            noiseSuppression,
            echoCancellation: echoCancel,
          },
        })
        .then((stream) => {
          streamRef.current = stream
          startRecorderWithStream(stream)
        })
    } else {
      startRecorderWithStream(streamRef.current)
    }
  }

  // Helper to force stop all audio tracks by requesting a new stream and stopping its tracks
  async function forceStopAllAudioTracks(): Promise<void> {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop()
        })
        streamRef.current = undefined
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: stereo ? 2 : 1,
          autoGainControl: autoGain,
          noiseSuppression,
          echoCancellation: echoCancel,
        },
      })
      stream.getTracks().forEach((track) => {
        track.stop()
      })
    } catch (err) {
      console.error(err)
    }
  }

  const stopRecording = async (): Promise<void> => {
    setIsRecording(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = undefined
    }
    if (recorder) {
      const { blob } = await recorder.stop()
      const url = URL.createObjectURL(blob)
      setAudioUrl(url)
      if (shouldSaveRecordingRef.current) {
        setRecordedAudio(url)
      }
      setRecordOpen(false)
      recorder = null
    }
    await forceStopAllAudioTracks()
  }

  const handleRecordClick = useCallback((): void => {
    if (!isRecording && countdown === undefined) {
      void navigator.mediaDevices
        .getUserMedia({
          audio: {
            channelCount: stereo ? 2 : 1,
            autoGainControl: autoGain,
            noiseSuppression,
            echoCancellation: echoCancel,
          },
        })
        .then((stream) => {
          streamRef.current = stream
          setCountdown(countIn)
          let current = countIn
          countdownIntervalRef.current = setInterval(() => {
            current -= 1
            if (current > 0) {
              setCountdown(current)
            } else {
              if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current)
                countdownIntervalRef.current = undefined
              }
              setCountdown()
              startRecording()
            }
          }, 1000)
        })
        .catch(() => {
          setCountdown()
        })
      return
    }
    if (isRecording) {
      setIsRecording(false)
      shouldSaveRecordingRef.current = true
      void stopRecording()
    }
  }, [
    isRecording,
    setIsRecording,
    setRecordOpen,
    setRecordedAudio,
    countdown,
    countIn,
  ])

  const handleClose = (): void => {
    setIsRecording(false)
    setRecordOpen(false)
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = undefined
    }
    setCountdown()
  }

  return {
    settingsOpen,
    setSettingsOpen,
    frameRef,
    isRecording,
    audioUrl,
    recordingTime,
    countdown,
    handleRecordClick,
    startRecording,
    stopRecording: handleClose,
    formatTime,
    handleClose,
    streamRef,
  }
}
